import {
	faChevronDown,
	faChevronUp,
	faMagnifyingGlass,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	ItemFacet,
	PortalCatalogFilterFieldDto,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { type ReactElement, useEffect, useId, useState } from 'react';
import { facetParamName } from '../../lib/catalogFacetFilters';
import {
	countActiveFacetSelections,
	isFacetSectionOpen,
} from '../../lib/filterRailSections';
import {
	buildTermsFacetRows,
	FACET_SEARCH_MIN_ROW_COUNT,
	selectVisibleTermsFacetRows,
} from '../../lib/termsFacetRows';

interface FilterRailProps {
	/** The site's exposed filter fields, in display order (from /catalog/config). */
	fields: Array<PortalCatalogFilterFieldDto>;
	/** Facet counts/bounds from the faceted search response (keyed by field key). */
	facets: Array<ItemFacet>;
	/** Current URL params — the single source of filter state. */
	searchParams: URLSearchParams;
	/**
	 * Apply a URL mutation. The page wraps setSearchParams and resets the page
	 * param, so every control here just mutates its own params.
	 */
	onUpdate: (mutate: (next: URLSearchParams) => void) => void;
	/**
	 * The shopper's open/closed choice for each headed section (terms, range
	 * and date filters), keyed by field key; a missing key means the section
	 * has not been toggled yet. Held by the page so the desktop rail and the
	 * mobile sheet, which unmounts when it closes, share it for the visit.
	 */
	sectionToggles: Record<string, boolean | undefined>;
	onSectionToggle: (fieldKey: string, open: boolean) => void;
}

/**
 * The catalog's faceted filter rail: renders the storefront's configured
 * filters (per-site subset/order/labels resolved server-side) with live facet
 * counts from the current result set. Filter state lives in the URL — this
 * component only reads params and emits mutations.
 *
 * Terms, range and date filters render as collapsible sections that start
 * collapsed unless they hold an active filter (see filterRailSections).
 * Boolean and dynamic toggles are a single checkbox row and stay as they are.
 */
export function FilterRail({
	fields,
	facets,
	searchParams,
	onUpdate,
	sectionToggles,
	onSectionToggle,
}: FilterRailProps): ReactElement | null {
	const { t } = useTranslation();
	// The rail is mounted twice (desktop sidebar and mobile sheet), so the
	// section body ids need a per-instance prefix to stay unique.
	const sectionIdPrefix = useId();
	// `| undefined` in the record types: index reads legitimately miss (a
	// field never expanded, a draft never typed) and the code branches on it.
	const [expandedFields, setExpandedFields] = useState<
		Record<string, boolean | undefined>
	>({});
	// Range/date inputs are draft-local until committed (blur / Enter) so we
	// don't rewrite the URL — and refetch — on every keystroke. Drafts reset
	// whenever the URL changes: any URL change means the previous interaction
	// completed (typing commits on blur before another control can act).
	const [drafts, setDrafts] = useState<Record<string, string | undefined>>({});
	// Type-to-search text per terms facet. Deliberately NOT reset when the URL
	// changes, so a shopper can search "pfizer" and tick several matches.
	const [facetSearchText, setFacetSearchText] = useState<
		Record<string, string | undefined>
	>({});

	useEffect(() => {
		setDrafts({});
	}, [searchParams]);

	if (fields.length === 0) {
		return null;
	}

	const facetByKey = new Map(facets.map((facet) => [facet.key, facet]));

	// Working in a section pins it open. Without this, a section that was open
	// only because it held a filter would snap shut the moment the shopper
	// cleared that filter to choose a different one.
	const keepSectionOpen = (fieldKey: string): void => {
		if (sectionToggles[fieldKey] !== true) {
			onSectionToggle(fieldKey, true);
		}
	};

	const toggleTermsValue = (
		field: PortalCatalogFilterFieldDto,
		value: string,
	): void => {
		const param = facetParamName(field);

		keepSectionOpen(field.key);
		onUpdate((next) => {
			const current = next.getAll(param);
			next.delete(param);

			const selected = current.includes(value)
				? current.filter((entry) => entry !== value)
				: [...current, value];

			for (const entry of selected) {
				next.append(param, entry);
			}
		});
	};

	const toggleBoolean = (field: PortalCatalogFilterFieldDto): void => {
		const param = facetParamName(field);

		onUpdate((next) => {
			if (next.get(param) === 'true') {
				next.delete(param);
			} else {
				next.set(param, 'true');
			}
		});
	};

	const commitDraft = (fieldKey: string, param: string, raw: string): void => {
		const trimmed = raw.trim();

		keepSectionOpen(fieldKey);
		onUpdate((next) => {
			if (trimmed.length === 0) {
				next.delete(param);
			} else {
				next.set(param, trimmed);
			}
		});
	};

	const setDateParam = (
		fieldKey: string,
		param: string,
		value: string,
	): void => {
		keepSectionOpen(fieldKey);
		onUpdate((next) => {
			if (value.length === 0) {
				next.delete(param);
			} else {
				next.set(param, value);
			}
		});
	};

	const renderCheckboxRow = (
		key: string,
		label: string,
		checked: boolean,
		count: number | null,
		onToggle: () => void,
	): ReactElement => (
		<label
			key={key}
			className="flex items-center gap-2 px-1 py-1 rounded cursor-pointer text-sm text-strong hover:bg-sunken"
		>
			<input
				type="checkbox"
				checked={checked}
				onChange={onToggle}
				className="h-3.5 w-3.5 shrink-0 accent-current"
			/>
			<span className="flex-1 min-w-0 truncate">{label}</span>
			{count != null && (
				<span className="text-xs text-brand-muted shrink-0">
					{t(String(count))}
				</span>
			)}
		</label>
	);

	/**
	 * A headed filter as a disclosure: the whole header row toggles it, like
	 * the Categories section above the rail. While collapsed with a filter set,
	 * the header carries the count so the narrowing is never invisible. The
	 * chevron follows the application's convention (up while closed, down
	 * while open, PD-15805), which the Categories header already uses.
	 */
	const renderSection = (
		field: PortalCatalogFilterFieldDto,
		body: ReactElement,
	): ReactElement => {
		const activeCount = countActiveFacetSelections(field, searchParams);
		const isOpen = isFacetSectionOpen(sectionToggles[field.key], activeCount);
		const bodyId = `${sectionIdPrefix}-${field.key}`;

		return (
			<div key={field.key}>
				<button
					type="button"
					onClick={() => {
						onSectionToggle(field.key, !isOpen);
					}}
					aria-expanded={isOpen}
					aria-controls={bodyId}
					className="w-full flex items-center justify-between gap-2 px-1 py-1 rounded text-left hover:bg-sunken"
				>
					<span className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-brand-muted">
						{t(field.label)}
						{!isOpen && activeCount > 0 && (
							<span className="ml-1 text-portal-accent">
								({t(String(activeCount))})
							</span>
						)}
					</span>
					<FontAwesomeIcon
						icon={isOpen ? faChevronDown : faChevronUp}
						className="shrink-0 text-[10px] text-brand-muted"
					/>
				</button>
				<div id={bodyId} hidden={!isOpen} className="mt-1">
					{body}
				</div>
			</div>
		);
	};

	return (
		<div className="space-y-3">
			{fields.map((field) => {
				const facet = facetByKey.get(field.key);

				// Dynamic toggles: plain on/off, no counts.
				if (field.resolution === 'dynamic') {
					const param = facetParamName(field);

					return (
						<div key={field.key}>
							{renderCheckboxRow(
								field.key,
								t(field.label),
								searchParams.get(param) === 'true',
								null,
								() => {
									toggleBoolean(field);
								},
							)}
						</div>
					);
				}

				if (field.kind === 'terms') {
					// Fixed-option custom fields fall back to their configured
					// options when the facet hasn't loaded yet; otherwise buckets
					// come from the live aggregation.
					const buckets =
						facet != null && facet.kind === 'terms'
							? facet.buckets
							: (field.options ?? []).map((option) => ({
									value: option.value,
									label: option.label,
									count: null as number | null,
								}));

					// Multi-select (PD-16898): ticked values sit at the top and
					// always render, even when the current results no longer
					// include them, so they can be seen and unticked.
					const param = facetParamName(field);
					const rows = buildTermsFacetRows(buckets, searchParams.getAll(param));

					if (rows.length === 0) {
						return null;
					}

					const expanded = expandedFields[field.key] === true;
					const searchText = facetSearchText[field.key] ?? '';
					const isSearching = searchText.trim().length > 0;
					const { visible } = selectVisibleTermsFacetRows(rows, {
						expanded,
						searchText,
					});
					// Whether the collapsed list cuts anything decides if the
					// Show more / Show fewer toggle is needed at all.
					const { hiddenCount: collapsedHiddenCount } =
						selectVisibleTermsFacetRows(rows, {
							expanded: false,
							searchText: '',
						});
					const hasSearchMatches = visible.some((entry) => !entry.selected);

					return renderSection(
						field,
						<>
							{rows.length > FACET_SEARCH_MIN_ROW_COUNT && (
								<div className="relative mb-1 px-1">
									<FontAwesomeIcon
										icon={faMagnifyingGlass}
										className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-brand-muted pointer-events-none"
									/>
									<input
										type="search"
										value={searchText}
										onChange={(e) => {
											setFacetSearchText((prev) => ({
												...prev,
												[field.key]: e.target.value,
											}));
										}}
										placeholder={t('Search')}
										aria-label={`${t('Search')} ${t(field.label)}`}
										className="search-on-surface w-full min-w-0 rounded border border-brand-border bg-brand-surface pl-7 pr-2 py-1 text-sm text-strong"
									/>
								</div>
							)}
							{/* A long facet (hundreds of manufacturers) scrolls inside
							    the section instead of pushing the rest of the rail
							    off screen once it is expanded or searched. */}
							<div
								className={`space-y-0.5${
									expanded || isSearching ? ' max-h-72 overflow-y-auto' : ''
								}`}
							>
								{visible.map((entry) =>
									renderCheckboxRow(
										entry.value,
										t(entry.label),
										entry.selected,
										entry.count,
										() => {
											toggleTermsValue(field, entry.value);
										},
									),
								)}
							</div>
							{isSearching && !hasSearchMatches && (
								<p className="px-1 py-1 text-xs text-brand-muted">
									{t('No matches')}
								</p>
							)}
							{!isSearching && collapsedHiddenCount > 0 && (
								<button
									type="button"
									onClick={() => {
										setExpandedFields((prev) => ({
											...prev,
											[field.key]: !expanded,
										}));
									}}
									className="mt-1 px-1 flex items-center gap-1 text-xs text-portal-accent"
								>
									<FontAwesomeIcon
										icon={expanded ? faChevronUp : faChevronDown}
										className="text-[10px]"
									/>
									{expanded ? t('Show fewer') : t('Show more')}
								</button>
							)}
						</>,
					);
				}

				if (field.kind === 'boolean') {
					const param = facetParamName(field);
					const trueCount =
						facet != null && facet.kind === 'boolean' ? facet.trueCount : null;

					return (
						<div key={field.key}>
							{renderCheckboxRow(
								field.key,
								t(field.label),
								searchParams.get(param) === 'true',
								trueCount,
								() => {
									toggleBoolean(field);
								},
							)}
						</div>
					);
				}

				if (field.kind === 'range') {
					const minParam = facetParamName(field, 'min');
					const maxParam = facetParamName(field, 'max');
					const bounds = facet != null && facet.kind === 'range' ? facet : null;

					// No item in the current result set has a value → dead control,
					// hide it (mirrors the empty-buckets rule for terms). EXCEPT
					// when the URL carries an active value for this field — the
					// visitor must always be able to see and clear a filter that
					// is narrowing their results. `facet == null` (counts not
					// loaded yet) keeps rendering to avoid a layout jump.
					if (
						bounds != null &&
						bounds.min == null &&
						bounds.max == null &&
						searchParams.get(minParam) == null &&
						searchParams.get(maxParam) == null
					) {
						return null;
					}

					const minPlaceholder =
						bounds?.min != null ? String(bounds.min) : t('Min');
					const maxPlaceholder =
						bounds?.max != null ? String(bounds.max) : t('Max');

					const draftValue = (param: string): string =>
						drafts[param] ?? searchParams.get(param) ?? '';

					const rangeInput = (
						param: string,
						placeholder: string,
						ariaLabel: string,
					): ReactElement => (
						<input
							type="number"
							inputMode="decimal"
							value={draftValue(param)}
							placeholder={placeholder}
							aria-label={ariaLabel}
							onChange={(e) => {
								setDrafts((prev) => ({ ...prev, [param]: e.target.value }));
							}}
							onBlur={(e) => {
								if (drafts[param] != null) {
									commitDraft(field.key, param, e.target.value);
								}
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									commitDraft(
										field.key,
										param,
										(e.target as HTMLInputElement).value,
									);
								}
							}}
							className="w-full min-w-0 rounded border border-brand-border bg-brand-surface px-2 py-1 text-sm text-strong"
						/>
					);

					return renderSection(
						field,
						<div className="flex items-center gap-2 px-1">
							{rangeInput(
								minParam,
								minPlaceholder,
								`${t(field.label)} ${t('minimum')}`,
							)}
							<span className="text-xs text-brand-muted">{t('to')}</span>
							{rangeInput(
								maxParam,
								maxPlaceholder,
								`${t(field.label)} ${t('maximum')}`,
							)}
						</div>,
					);
				}

				// Only 'date' remains — the earlier returns exhaust every other kind.
				const fromParam = facetParamName(field, 'from');
				const toParam = facetParamName(field, 'to');
				const dateBounds =
					facet != null && facet.kind === 'date' ? facet : null;

				// Same data-less hiding rule as range above.
				if (
					dateBounds != null &&
					dateBounds.min == null &&
					dateBounds.max == null &&
					searchParams.get(fromParam) == null &&
					searchParams.get(toParam) == null
				) {
					return null;
				}

				const dateInput = (param: string, ariaLabel: string): ReactElement => (
					<input
						type="date"
						value={searchParams.get(param) ?? ''}
						aria-label={ariaLabel}
						onChange={(e) => {
							setDateParam(field.key, param, e.target.value);
						}}
						className="w-full min-w-0 rounded border border-brand-border bg-brand-surface px-2 py-1 text-sm text-strong"
					/>
				);

				return renderSection(
					field,
					<div className="space-y-1.5 px-1">
						{dateInput(fromParam, `${t(field.label)} ${t('from')}`)}
						{dateInput(toParam, `${t(field.label)} ${t('to')}`)}
					</div>,
				);
			})}
		</div>
	);
}
